/**
 * Storage Health Check and Recovery Utilities
 *
 * Detects and recovers from browser storage corruption issues (IndexedDB, localStorage)
 * that can cause authentication hangs.
 *
 * @module storage-health
 */
/* eslint-env browser */

import { systemLogger } from '@/lib/logging';

// ============================================================================
// TYPES
// ============================================================================

export interface StorageHealthResult {
  /** Whether localStorage read/write operations succeed */
  localStorageOk: boolean;
  /** Whether IndexedDB can be opened and closed */
  indexedDBOk: boolean;
  /** Whether the Supabase auth token key is accessible (not corrupted) */
  supabaseKeyAccessible: boolean;
  /** Whether recovery is recommended based on health checks */
  needsRecovery: boolean;
  /** Diagnostic details for logging */
  diagnostics: {
    localStorageError?: string;
    indexedDBError?: string;
    supabaseKeyError?: string;
  };
}

// ============================================================================
// STORAGE KEY DETECTION
// ============================================================================

/**
 * Get the Supabase storage key based on the configured URL
 * Supabase stores auth tokens in localStorage with key: sb-{project-ref}-auth-token
 */
export function getSupabaseStorageKey(): string {
  try {
    const supabaseUrl = import.meta.env['VITE_PUBLIC_SUPABASE_URL'] as string | undefined;
    if (!supabaseUrl) {
      systemLogger.warn('[storage-health] VITE_PUBLIC_SUPABASE_URL not found');
      return 'sb-unknown-auth-token';
    }

    // Extract project ref from URL: https://abcdefghijk.supabase.co -> abcdefghijk
    const urlParts = supabaseUrl.split('//');
    const hostParts = urlParts[1]?.split('.') || [];
    const projectRef = hostParts[0] || 'unknown';

    return `sb-${projectRef}-auth-token`;
  } catch (_error) {
    systemLogger.error('[storage-health] Failed to determine Supabase storage key', _error);
    return 'sb-unknown-auth-token';
  }
}

// ============================================================================
// HEALTH CHECK FUNCTIONS
// ============================================================================

/**
 * Test if localStorage is accessible and functional
 * Returns true if read/write operations succeed
 */
export function testLocalStorage(): boolean {
  const testKey = '__storage_health_check__';
  const testValue = `test_${Date.now()}`;

  try {
    // Test write
    localStorage.setItem(testKey, testValue);

    // Test read
    const readValue = localStorage.getItem(testKey);

    // Test delete
    localStorage.removeItem(testKey);

    // Verify read succeeded
    return readValue === testValue;
  } catch (_error) {
    systemLogger.warn('[storage-health] localStorage test failed', _error);
    return false;
  }
}

/**
 * Test if IndexedDB is accessible and functional
 * Uses a timeout to detect hung operations (common with corrupted IndexedDB)
 *
 * @param timeoutMs - Maximum time to wait for IndexedDB operations (default: 500ms)
 */
export async function testIndexedDB(timeoutMs = 500): Promise<boolean> {
  const testDbName = '__storage_health_check__';

  return Promise.race([
    new Promise<boolean>((resolve) => {
      try {
        const request = window.indexedDB.open(testDbName, 1);

        request.onerror = () => {
          systemLogger.warn('[storage-health] IndexedDB open failed', request.error);
          resolve(false);
        };

        request.onsuccess = () => {
          try {
            // Successfully opened, now close and delete
            request.result.close();

            const deleteRequest = window.indexedDB.deleteDatabase(testDbName);
            deleteRequest.onsuccess = () => resolve(true);
            deleteRequest.onerror = () => {
              // Delete failed but open succeeded - still consider healthy
              systemLogger.warn('[storage-health] IndexedDB delete test db failed (non-critical)');
              resolve(true);
            };
          } catch (_closeError) {
            systemLogger.warn('[storage-health] IndexedDB close failed', _closeError);
            resolve(false);
          }
        };

        request.onblocked = () => {
          systemLogger.warn('[storage-health] IndexedDB blocked');
          resolve(false);
        };
      } catch (_error) {
        systemLogger.warn('[storage-health] IndexedDB test threw exception', _error);
        resolve(false);
      }
    }),
    new Promise<boolean>((resolve) => {
      setTimeout(() => {
        systemLogger.warn(`[storage-health] IndexedDB test timed out after ${timeoutMs}ms`);
        resolve(false);
      }, timeoutMs);
    }),
  ]);
}

/**
 * Test if the Supabase auth token is accessible (not corrupted JSON)
 */
export function testSupabaseKeyAccessible(): boolean {
  try {
    const storageKey = getSupabaseStorageKey();
    const storedValue = localStorage.getItem(storageKey);

    // If no value stored, that's fine - not corrupted
    if (!storedValue) {
      return true;
    }

    // Try to parse as JSON to detect corruption
    JSON.parse(storedValue);
    return true;
  } catch (_error) {
    systemLogger.warn('[storage-health] Supabase auth token appears corrupted', _error);
    return false;
  }
}

/**
 * Run comprehensive storage health check
 * Detects issues with localStorage, IndexedDB, and Supabase auth token
 */
export async function checkStorageHealth(): Promise<StorageHealthResult> {
  systemLogger.debug('[storage-health] Starting storage health check...');

  const diagnostics: StorageHealthResult['diagnostics'] = {};

  // Test localStorage
  const localStorageOk = testLocalStorage();
  if (!localStorageOk) {
    diagnostics.localStorageError = 'localStorage read/write test failed';
  }

  // Test IndexedDB (with timeout protection)
  const indexedDBOk = await testIndexedDB();
  if (!indexedDBOk) {
    diagnostics.indexedDBError = 'IndexedDB open/close test failed or timed out';
  }

  // Test Supabase key accessibility
  const supabaseKeyAccessible = testSupabaseKeyAccessible();
  if (!supabaseKeyAccessible) {
    diagnostics.supabaseKeyError = 'Supabase auth token appears corrupted';
  }

  // Determine if recovery is needed
  // Note: In incognito mode, tests may fail but that's not corruption
  // We only recommend recovery if:
  // 1. IndexedDB specifically times out (signature of corruption)
  // 2. Supabase key is corrupted (invalid JSON)
  const needsRecovery = !indexedDBOk || !supabaseKeyAccessible;

  const result: StorageHealthResult = {
    localStorageOk,
    indexedDBOk,
    supabaseKeyAccessible,
    needsRecovery,
    diagnostics,
  };

  systemLogger.debug('[storage-health] Health check complete', result);

  return result;
}

// ============================================================================
// RECOVERY FUNCTIONS
// ============================================================================

/**
 * Clear only auth-related storage (preserves translations and other preferences)
 * This is the targeted recovery approach - first line of defense
 *
 * Clears:
 * - Supabase auth token from localStorage
 * - permission_security_key from sessionStorage
 * - All IndexedDB databases (Supabase stores session data here)
 *
 * Preserves:
 * - Translation preferences
 * - UI preferences
 * - Other non-auth localStorage items
 *
 * @returns true if recovery succeeded, false if it failed
 */
export async function clearAuthStorage(): Promise<boolean> {
  systemLogger.info('[storage-health] Starting targeted auth storage recovery...');

  let success = true;

  // 1. Clear Supabase auth token from localStorage
  try {
    const storageKey = getSupabaseStorageKey();
    localStorage.removeItem(storageKey);
    systemLogger.debug(`[storage-health] Cleared localStorage key: ${storageKey}`);
  } catch (_error) {
    systemLogger.error('[storage-health] Failed to clear Supabase auth token', _error);
    success = false;
  }

  // 2. Clear permission security key from sessionStorage
  try {
    sessionStorage.removeItem('permission_security_key');
    systemLogger.debug('[storage-health] Cleared sessionStorage permission key');
  } catch (_error) {
    systemLogger.error('[storage-health] Failed to clear sessionStorage', _error);
    success = false;
  }

  // 3. Clear IndexedDB databases (Supabase stores session data here)
  try {
    await clearIndexedDB();
    systemLogger.debug('[storage-health] Cleared IndexedDB databases');
  } catch (_error) {
    systemLogger.error('[storage-health] Failed to clear IndexedDB', _error);
    success = false;
  }

  systemLogger.info(`[storage-health] Targeted recovery ${success ? 'succeeded' : 'failed'}`);
  return success;
}

/**
 * Clear ALL browser storage for this domain (nuclear option)
 * Use this when targeted recovery fails
 *
 * Clears:
 * - All localStorage
 * - All sessionStorage
 * - All IndexedDB databases
 * - All cookies for this domain
 *
 * @returns true if recovery succeeded, false if it failed
 */
export async function clearAllStorage(): Promise<boolean> {
  systemLogger.warn('[storage-health] Starting nuclear storage recovery (clearing everything)...');

  let success = true;

  // 1. Clear all localStorage
  try {
    localStorage.clear();
    systemLogger.debug('[storage-health] Cleared all localStorage');
  } catch (_error) {
    systemLogger.error('[storage-health] Failed to clear localStorage', _error);
    success = false;
  }

  // 2. Clear all sessionStorage
  try {
    sessionStorage.clear();
    systemLogger.debug('[storage-health] Cleared all sessionStorage');
  } catch (_error) {
    systemLogger.error('[storage-health] Failed to clear sessionStorage', _error);
    success = false;
  }

  // 3. Clear all IndexedDB databases
  try {
    await clearIndexedDB();
    systemLogger.debug('[storage-health] Cleared all IndexedDB databases');
  } catch (_error) {
    systemLogger.error('[storage-health] Failed to clear IndexedDB', _error);
    success = false;
  }

  // 4. Clear cookies for this domain
  try {
    clearCookies();
    systemLogger.debug('[storage-health] Cleared cookies');
  } catch (_error) {
    systemLogger.error('[storage-health] Failed to clear cookies', _error);
    // Non-critical - don't mark as failure
  }

  systemLogger.warn(`[storage-health] Nuclear recovery ${success ? 'succeeded' : 'failed'}`);
  return success;
}

/**
 * Clear all IndexedDB databases for this origin
 */
export async function clearIndexedDB(): Promise<void> {
  // Check if window.indexedDB.databases() is supported (not in all browsers)
  if (typeof window.indexedDB.databases === 'function') {
    try {
      const databases = await window.indexedDB.databases();

      await Promise.all(
        databases.map(
          (db) =>
            new Promise<void>((resolve) => {
              if (!db.name) {
                resolve();
                return;
              }

              const request = window.indexedDB.deleteDatabase(db.name);
              request.onsuccess = () => {
                systemLogger.debug(`[storage-health] Deleted IndexedDB: ${db.name}`);
                resolve();
              };
              request.onerror = () => {
                systemLogger.warn(`[storage-health] Failed to delete IndexedDB: ${db.name}`);
                resolve(); // Don't reject - continue with other databases
              };
              request.onblocked = () => {
                systemLogger.warn(`[storage-health] IndexedDB deletion blocked: ${db.name}`);
                resolve();
              };
            })
        )
      );
    } catch (_error) {
      systemLogger.error('[storage-health] window.indexedDB.databases() failed', _error);
      throw _error;
    }
  } else {
    // Fallback for browsers that don't support window.indexedDB.databases()
    // Try to delete known Supabase database names
    const knownDbNames = ['supabase-auth', 'supabase'];

    for (const dbName of knownDbNames) {
      try {
        const request = window.indexedDB.deleteDatabase(dbName);
        await new Promise<void>((resolve) => {
          request.onsuccess = () => resolve();
          request.onerror = () => resolve();
          request.onblocked = () => resolve();
        });
      } catch (_error) {
        // Ignore errors for fallback approach
      }
    }

    systemLogger.debug('[storage-health] Used fallback IndexedDB clearing (databases() not supported)');
  }
}

/**
 * Clear cookies for the current domain
 */
function clearCookies(): void {
  const cookies = document.cookie.split(';');

  for (const cookie of cookies) {
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();

    if (name) {
      // Clear with various path options to ensure removal
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
    }
  }
}

// ============================================================================
// UTILITY EXPORTS
// ============================================================================

/**
 * Check if browser is in incognito/private mode
 * Storage may be limited in this mode (not corruption)
 */
export async function isIncognitoMode(): Promise<boolean> {
  // Try IndexedDB estimation - limited in incognito
  if ('storage' in navigator && 'estimate' in navigator.storage) {
    try {
      const estimate = await navigator.storage.estimate();
      // Very low quota suggests incognito mode
      if (estimate.quota && estimate.quota < 120000000) {
        return true;
      }
    } catch (_error) {
      // Estimation not available
    }
  }

  return false;
}
